/* D3 · what the paywall leads with.
 *
 * It used to lead with an inventory: "150+ deeper variations". That is a fact
 * about our catalog, and nobody buys a catalog — the reason to want deeper
 * theory is that your own games are already going deeper than the free lines
 * do. Every number needed to say so has been on disk since workstream E: the
 * coaching record stores each leak's engine-graded cost, how deep in the game
 * it happens, and whether any course covers it.
 *
 * Two rules shape the output, and both are about not overselling:
 *
 *   1. **It states the user's situation, never a promise about the product.**
 *      "Your worst open leak costs about a pawn" is checkable by the person
 *      reading it. "Pro will fix your leaks" is not, and one unfalsifiable
 *      sentence next to the price is how a paywall stops being believed.
 *      The PERKS list under it is where the product describes itself.
 *   2. **No rating-point conversion.** The obvious next sentence is "…roughly
 *      40 rating points", and the app cannot defend the arithmetic: centipawns
 *      lost in one opening position do not map onto Elo by any constant this
 *      codebase can measure. product_improvements_v1.md's D3 sketch used that
 *      phrasing; it is the one part of the sketch deliberately not built.
 *
 * Returns null whenever there is nothing true to say — a user with no games,
 * no leaks, or only ungradable ones sees the perk list alone, which is the
 * honest fallback rather than a placeholder.
 *
 * Pure: record in, two strings out. No engine, no catalog, no React — it runs
 * inside ProModal, which lives in the main chunk and must stay clear of the
 * course data (see persist.js's header and lazyChunks.test.js).
 */
import { MIN_CP_LOSS, severityLabel } from "./cost.js";
import { t } from "../i18n.js";

/* Full moves, as a player counts them. Same rule as debrief.js#moveNumber;
 * duplicated rather than imported because debrief.js is Rival-game code and
 * this is paywall copy — the shared thing between them is arithmetic, not a
 * concept, and a dependency between the two would be the wrong kind of link. */
const moveNumber = (ply) => Math.floor(ply / 2) + 1;

const isOpen = (r) => r && (r.status === "active" || r.status === "improving");

/* The surfaces that sell Level 3 lines, as opposed to the ones that sell the
 * depth of the mined deck. The lead swaps between them: someone who pressed a
 * blurred Master line is asking about theory, and answering with "your deck is
 * capped at 20" would be answering a question they did not ask. */
const CATALOG_SOURCES = new Set(["tier_header", "locked_line"]);

export function summarizeLeaks(record) {
  const open = Object.values(record || {}).filter(isOpen);
  const graded = open.filter((r) => Number.isFinite(r.costCp) && r.costCp >= MIN_CP_LOSS);
  const worst = graded.reduce((best, r) => (!best || r.costCp > best.costCp ? r : best), null);
  const depths = open.map((r) => (Array.isArray(r.context) ? moveNumber(r.context.length) : null)).filter(Number.isFinite);
  return {
    open: open.length,
    graded: graded.length,
    worstCp: worst ? worst.costCp : null,
    /* The EARLIEST open leak, not the average: "from move 6 on" is a claim the
     * user can check against the one they remember, and an average over six
     * positions is a number that describes none of them. */
    fromMove: depths.length ? Math.min(...depths) : null,
    /* Leaks in positions no course covers. Not sold as locked content — they
     * are drillable for free and always have been (D4) — but they are the
     * honest measure of how far past the catalog this player's games go. */
    uncovered: open.filter((r) => !r.courseId).length,
  };
}

export function proPitch({ record, belowCap = 0, source } = {}) {
  const s = summarizeLeaks(record);
  // Every branch below is a complete sentence per count, rather than an
  // English "add -s" suffix or a trailing clause spliced onto a shared
  // template — the singular case also needs "sits" where the plural needs
  // "sit", which concatenation can't express either (O3, product_improvements_v2.md).
  const deckDepth =
    belowCap > 0
      ? {
          lead: t("proPitch.belowCap", { count: belowCap, open: s.open || "20" }),
          detail: t("proPitch.belowCapDetail"),
        }
      : null;

  const severity = severityLabel(s.worstCp);
  const gradedDetail = () => {
    if (s.open <= 1) return t("proPitch.gradedDetail");
    return s.fromMove
      ? t("proPitch.gradedFromMove", { graded: s.graded, open: s.open, fromMove: s.fromMove })
      : t("proPitch.graded", { graded: s.graded, open: s.open });
  };
  const leaks = severity
    ? { lead: t("proPitch.worstLeak", { severity }), detail: gradedDetail() }
    : s.fromMove && s.open
      ? {
          lead: t("proPitch.openLeaks", { count: s.open, fromMove: s.fromMove }),
          detail: t("proPitch.mined"),
        }
      : null;

  const order = CATALOG_SOURCES.has(source) ? [leaks, deckDepth] : [deckDepth, leaks];
  return order.find(Boolean) || null;
}
